/**
 * Client-side image compression.
 * Resizes images to max 1024px on longest edge and re-encodes to 70% JPEG.
 * Returns a base64 string (no data: prefix) suitable for JSON payloads.
 *
 * Usage:
 *   const base64 = await compressImage(file);
 *   await api.post('/attendance/mark', { photo_base64: base64 });
 */
export async function compressImage(fileOrDataUrl, { maxEdge = 1024, quality = 0.7, mime = 'image/jpeg' } = {}) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        let { width, height } = img;
        if (width > maxEdge || height > maxEdge) {
          const scale = maxEdge / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL(mime, quality);
        resolve(dataUrl.split(',')[1]); // strip "data:image/jpeg;base64,"
      } catch (e) { reject(e); }
    };
    img.onerror = reject;
    if (typeof fileOrDataUrl === 'string') {
      img.src = fileOrDataUrl;
    } else {
      const reader = new FileReader();
      reader.onload = (e) => { img.src = e.target.result; };
      reader.onerror = reject;
      reader.readAsDataURL(fileOrDataUrl);
    }
  });
}

/** Thin wrapper that returns the full data-URL (for <img src=>) */
export async function compressImageDataUrl(fileOrDataUrl, opts) {
  const b64 = await compressImage(fileOrDataUrl, opts);
  return `data:${opts?.mime || 'image/jpeg'};base64,${b64}`;
}
